import React from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { CheckCircleOutlined, CopyOutlined, ExportOutlined, EyeInvisibleOutlined, EyeOutlined } from '@ant-design/icons';
import { DeploymentStatus, WordPressConfig } from '../../types/wordpress.type';

const WordPressSuccess: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [showPassword, setShowPassword] = React.useState(false); // ✅ MOVIDO AQUÍ
  
  const state = location.state as { status: DeploymentStatus; config: WordPressConfig } | null;

  // ✅ Ahora las validaciones van DESPUÉS de todos los hooks
  React.useEffect(() => {
    if (!state || !state.status || !state.config) {
      navigate('/resources');
    }
  }, [state, navigate]);

  if (!state || !state.status || !state.config) {
    return null;
  }

  const { status, config } = state;

  const siteUrl = `https://${config.domain}`;
  const adminUrl = `${siteUrl}/wp-admin`;

  const copyToClipboard = (text: string, label: string) => {
    navigator.clipboard.writeText(text);
    alert(`${label} copied to clipboard!`);
  };

  return (
    <div className="max-w-[900px] mx-auto px-6">
      {/* Success Header */}
      <div className="text-center mb-8">
        <div className="inline-flex items-center justify-center w-20 h-20 bg-green-100 rounded-full mb-4">
          <CheckCircleOutlined className="text-5xl text-green-600" />
        </div>
        <h1 className="text-4xl font-bold text-gray-900 dark:text-white mb-2">
          WordPress Site Ready! 🎉
        </h1>
        <p className="text-gray-600 dark:text-gray-400 text-lg">
          Your WordPress site has been successfully deployed and is now live
        </p>
      </div>

      {/* Site Access Card */}
      <div className="bg-gradient-to-br from-blue-50 to-cyan-50 dark:from-blue-900/20 dark:to-cyan-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-8 mb-6">
        <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-6 flex items-center gap-2">
          <span>🌐</span> Site Access
        </h2>
        
        <div className="space-y-4">
          {/* WordPress Site URL */}
          <div className="bg-white dark:bg-gray-800 rounded-lg p-4 border border-blue-100 dark:border-blue-800">
            <label className="text-sm font-medium text-gray-600 block mb-2">
              WordPress Site URL
            </label>
            <div className="flex items-center gap-2">
              <span className="flex-1 text-blue-600 font-mono text-sm break-all">
                {siteUrl}
              </span>
              <button
                onClick={() => copyToClipboard(siteUrl, 'Site URL')}
                className="p-2 text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white hover:bg-gray-100 dark:hover:bg-gray-700 rounded transition-colors"
                title="Copy URL"
              >
                <CopyOutlined />
              </button>
              <button
                onClick={() => window.open(siteUrl, '_blank')}
                className="p-2 text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white hover:bg-gray-100 dark:hover:bg-gray-700 rounded transition-colors"
                title="Open site"
              >
                <ExportOutlined />
              </button>
            </div>
          </div>

          {/* Admin Panel URL */}
          <div className="bg-white dark:bg-gray-800 rounded-lg p-4 border border-blue-100 dark:border-blue-800">
            <label className="text-sm font-medium text-gray-600 block mb-2">
              Admin Dashboard
            </label>
            <div className="flex items-center gap-2">
              <span className="flex-1 text-blue-600 font-mono text-sm break-all">
                {adminUrl}
              </span>
              <button
                onClick={() => copyToClipboard(adminUrl, 'Admin URL')}
                className="p-2 text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white hover:bg-gray-100 dark:hover:bg-gray-700 rounded transition-colors"
                title="Copy URL"
              >
                <CopyOutlined />
              </button>
              <button
                onClick={() => window.open(adminUrl, '_blank')}
                className="p-2 text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white hover:bg-gray-100 dark:hover:bg-gray-700 rounded transition-colors"
                title="Open admin"
              >
                <ExportOutlined />
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Credentials Card */}
      <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-6 mb-6">
        <h2 className="text-lg font-bold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
          <span>🔐</span> Login Credentials
        </h2>
        
        <div className="space-y-4">
          <div>
            <label className="text-sm font-medium text-gray-600 block mb-2">Username</label>
            <div className="flex items-center gap-2">
              <input
                type="text"
                value={config.adminUsername}
                readOnly
                className="flex-1 px-4 py-2 bg-gray-50 border border-gray-200 rounded-lg font-mono text-sm"
              />
              <button
                onClick={() => copyToClipboard(config.adminUsername, 'Username')}
                className="p-2 text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white hover:bg-gray-100 dark:hover:bg-gray-700 rounded transition-colors"
                title="Copy username"
              >
                <CopyOutlined />
              </button>
            </div>
          </div>

          <div>
            <label className="text-sm font-medium text-gray-600 block mb-2">Password</label>
            <div className="flex items-center gap-2">
              <input
                type={showPassword ? 'text' : 'password'}
                value={config.adminPassword}
                readOnly
                className="flex-1 px-4 py-2 bg-gray-50 border border-gray-200 rounded-lg font-mono text-sm"
              />
              <button
                onClick={() => setShowPassword(!showPassword)}
                className="p-2 text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white hover:bg-gray-100 dark:hover:bg-gray-700 rounded transition-colors"
                title="Toggle visibility"
              >
                {showPassword ? <EyeInvisibleOutlined /> : <EyeOutlined />}
              </button>
              <button
                onClick={() => copyToClipboard(config.adminPassword, 'Password')}
                className="p-2 text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white hover:bg-gray-100 dark:hover:bg-gray-700 rounded transition-colors"
                title="Copy password"
              >
                <CopyOutlined />
              </button>
            </div>
          </div>
        </div>

        <div className="mt-4 p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
          <p className="text-xs text-yellow-800">
            ⚠️ <strong>Important:</strong> Save these credentials in a secure location. For security reasons,
            we won't show them again.
          </p>
        </div>
      </div>

      {/* Site Details */}
      <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-6 mb-6">
        <h2 className="text-lg font-bold text-gray-900 dark:text-white mb-4">Site Configuration</h2>
        
        <div className="grid grid-cols-2 gap-4 text-sm">
          <div>
            <span className="text-gray-600 dark:text-gray-400">Site Name:</span>
            <p className="font-medium text-gray-900 dark:text-white">{config.name}</p>
          </div>
          <div>
            <span className="text-gray-600 dark:text-gray-400">Instance Type:</span>
            <p className="font-medium text-gray-900 dark:text-white">{config.instanceType}</p>
          </div>
          <div>
            <span className="text-gray-600 dark:text-gray-400">PHP Version:</span>
            <p className="font-medium text-gray-900 dark:text-white">{config.phpVersion}</p>
          </div>
          <div>
            <span className="text-gray-600 dark:text-gray-400">WordPress Version:</span>
            <p className="font-medium text-gray-900 dark:text-white">{config.wpVersion}</p>
          </div>
          <div>
            <span className="text-gray-600 dark:text-gray-400">SSL Certificate:</span>
            <p className="font-medium text-green-600">✓ Enabled</p>
          </div>
          <div>
            <span className="text-gray-600 dark:text-gray-400">CDN:</span>
            <p className="font-medium text-gray-900 dark:text-white">
              {config.enableCDN ? '✓ Enabled' : 'Disabled'}
            </p>
          </div>
        </div>
      </div>

      {/* Next Steps */}
      <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-6 mb-6">
        <h2 className="text-lg font-bold text-gray-900 dark:text-white mb-4">Next Steps</h2>
        
        <div className="space-y-3">
          <div className="flex items-start gap-3">
            <span className="flex-shrink-0 w-6 h-6 bg-black text-white rounded-full flex items-center justify-center text-xs font-bold">
              1
            </span>
            <div>
              <p className="font-medium text-gray-900 dark:text-white">Access your WordPress site</p>
              <p className="text-sm text-gray-600">Visit your site and explore the default theme</p>
            </div>
          </div>
          
          <div className="flex items-start gap-3">
            <span className="flex-shrink-0 w-6 h-6 bg-black text-white rounded-full flex items-center justify-center text-xs font-bold">
              2
            </span>
            <div>
              <p className="font-medium text-gray-900 dark:text-white">Log in to WordPress Admin</p>
              <p className="text-sm text-gray-600">Customize your site, install themes and plugins</p>
            </div>
          </div>
          
          <div className="flex items-start gap-3">
            <span className="flex-shrink-0 w-6 h-6 bg-black text-white rounded-full flex items-center justify-center text-xs font-bold">
              3
            </span>
            <div>
              <p className="font-medium text-gray-900 dark:text-white">Configure your domain (optional)</p>
              <p className="text-sm text-gray-600">Point your custom domain to this WordPress site</p>
            </div>
          </div>
        </div>
      </div>

      {/* Action Buttons */}
      <div className="flex gap-4 justify-center pb-8">
        <button
          onClick={() => window.open(siteUrl, '_blank')}
          className="px-8 py-3 bg-black text-white rounded-lg font-medium hover:bg-dark-bg transition-colors inline-flex items-center gap-2"
        >
          <ExportOutlined />
          Open WordPress Site
        </button>
        <button
          onClick={() => window.open(adminUrl, '_blank')}
          className="px-8 py-3 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-700 transition-colors inline-flex items-center gap-2"
        >
          <ExportOutlined />
          Access Admin Panel
        </button>
        <button
          onClick={() => navigate('/resources')}
          className="px-8 py-3 border border-gray-300 dark:border-gray-600 rounded-lg font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
        >
          Back to Resources
        </button>
      </div>
    </div>
  );
};

export default WordPressSuccess;